/**
 * Unified Onboarding Wizard
 *
 * Architecture (post-refactor):
 *   - Wizard step state lives in React component state only (no Table Storage session)
 *   - SharePoint WVHealth_ToolConfig.SetupComplete is the SINGLE SOURCE OF TRUTH
 *   - License API provides allowedSiteUrls (first URL = primary site)
 *   - One backend write at the very end: POST /api/onboarding-complete
 *
 * Flow:
 *   Mount → getBootstrapStatus() → if setupComplete → /dashboard
 *                                 → else → TenantSetup gate → wizard steps
 *
 * Steps:
 *   TenantSetup (gate) → Welcome → ConfirmOrg → ConnectSharePoint → RunScan → Complete
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import OnboardingLayout from '../../components/onboarding/Layout';
import TenantSetup from './TenantSetup';
import Welcome from './Welcome';
import ConfirmOrg from './ConfirmOrg';
import ConnectSharePoint from './ConnectSharePoint';
import RunScan from './RunScan';
import Complete from './Complete';
import { completeOnboarding } from '../../services/onboardingService';
import type { SiteEntry } from '../../services/onboardingService';
import { getBootstrapStatus } from '../../services/bootstrapService';
import { trackFeatureUsage, trackException, FeatureEvent, EventProp, ExceptionPhase, ExceptionProp } from '../../observability';

// ─── Types ───────────────────────────────────────────────────────────────────

type WizardStep =
    | 'loading'
    | 'TenantSetup'
    | 'Welcome'
    | 'ConfirmOrg'
    | 'ConnectSharePoint'
    | 'RunScan'
    | 'Complete';

// ─── Component ───────────────────────────────────────────────────────────────

export default function Onboarding() {
    const { accounts } = useMsal();
    const navigate = useNavigate();

    const [step, setStep] = useState<WizardStep>('loading');
    const [error, setError] = useState<string | null>(null);

    // Wizard data collected across steps (local state — no backend persistence)
    const [primarySiteUrl, setPrimarySiteUrl] = useState<string>('');
    const [selectedSites, setSelectedSites] = useState<SiteEntry[]>([]);

    // Prevent double-init in React StrictMode
    const initRef = useRef(false);

    // ── Auth guard ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (accounts.length === 0) navigate('/', { replace: true });
    }, [accounts, navigate]);

    // ── Bootstrap check on mount ──────────────────────────────────────────────
    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;

        const init = async () => {
            try {
                console.log('[Onboarding] Checking bootstrap status...');
                const status = await getBootstrapStatus();
                console.log('[Onboarding] Bootstrap status:', status);

                if (status.setupComplete) {
                    // Already set up — go straight to dashboard
                    console.log('[Onboarding] Setup complete, redirecting to dashboard');
                    navigate('/dashboard', { replace: true });
                } else {
                    // Not yet set up — start with the TenantSetup eligibility gate
                    console.log('[Onboarding] Setup not complete, starting wizard at TenantSetup');
                    trackFeatureUsage(FeatureEvent.OnboardingStarted);
                    setStep('TenantSetup');
                }
            } catch (err: any) {
                console.error('[Onboarding] Bootstrap check failed:', err);
                // On error, default to TenantSetup rather than blocking the user
                setStep('TenantSetup');
            }
        };

        init();
    }, [navigate]);

    // ── Finish handler ────────────────────────────────────────────────────────
    const handleFinish = async () => {
        try {
            setError(null);
            if (!primarySiteUrl) {
                throw new Error('Primary site URL not found. Please go back and complete the SharePoint connection step.');
            }
            await completeOnboarding(primarySiteUrl);
            trackFeatureUsage(FeatureEvent.OnboardingCompleted);
            navigate('/dashboard', { replace: true });
        } catch (err: any) {
            console.error('[Onboarding] Complete failed:', err);
            trackException(err, { [ExceptionProp.Phase]: ExceptionPhase.OnboardingComplete });
            setError(err.message);
        }
    };

    // ── ConnectSharePoint completion handler ──────────────────────────────────
    const handleSharePointConnected = (data: {
        primarySiteUrl: string;
        selectedSites: SiteEntry[];
        connectionState: string;
    }) => {
        setPrimarySiteUrl(data.primarySiteUrl);
        setSelectedSites(data.selectedSites);
        trackFeatureUsage(FeatureEvent.OnboardingStepCompleted, { [EventProp.Step]: 'ConnectSharePoint' });
        setStep('RunScan');
    };

    // ── Loading state ─────────────────────────────────────────────────────────
    if (step === 'loading') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2ba6bb] mx-auto" />
                    <p className="mt-4 text-gray-600">Loading…</p>
                </div>
            </div>
        );
    }

    // ── Error state ───────────────────────────────────────────────────────────
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white p-6 rounded-lg shadow max-w-md text-center">
                    <p className="text-red-600 mb-4">{error}</p>
                    <button
                        onClick={() => setError(null)}
                        className="text-sm text-[#2ba6bb] underline"
                    >
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    // ── Wizard ────────────────────────────────────────────────────────────────
    return (
        <OnboardingLayout currentStep={step}>

            {/* Gate: eligibility check before starting wizard */}
            {step === 'TenantSetup' && (
                <TenantSetup onNext={() => setStep('Welcome')} />
            )}

            {step === 'Welcome' && (
                <Welcome onNext={() => setStep('ConfirmOrg')} />
            )}

            {step === 'ConfirmOrg' && (
                <ConfirmOrg
                    onNext={() => setStep('ConnectSharePoint')}
                    onBack={() => setStep('Welcome')}
                />
            )}

            {step === 'ConnectSharePoint' && (
                <ConnectSharePoint
                    initialSites={selectedSites}
                    onNext={handleSharePointConnected}
                    onBack={() => setStep('ConfirmOrg')}
                />
            )}

            {step === 'RunScan' && (
                <RunScan
                    onNext={() => setStep('Complete')}
                    onBack={() => setStep('ConnectSharePoint')}
                />
            )}

            {step === 'Complete' && (
                <Complete onFinish={handleFinish} />
            )}

        </OnboardingLayout>
    );
}
